import React, { useEffect, useState } from 'react';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Icon from 'components/Utils/NewIcon';
import PaymentPlanSetup from 'components/Payments/PaymentPlanSetup';
import ServiceFees from 'components/Clinic/ServiceFees';
import MiscUtils from 'services/MiscUtils';
import { DefaultSwitch } from 'services/UIUtils';
import RouteUtils from 'services/RouteUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { IsStripeEnabled, IsContinuityOfCareEnabled } from 'careand-redux/selectors';
import { NavStack, usePathParams } from 'components/Navigation/NavStack';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import type { TenantOptions as TenantOptionsModel, TenantSocialLinks } from 'generated/api';

const OPTION_TABS = [
  { tab: 'organization', icon: 'building', label: 'Organization' },
  { tab: 'contact', icon: 'address-card', label: 'Contact & Links' },
  { tab: 'social', icon: 'share-nodes', label: 'Social Links' },
  { tab: 'appointments', icon: 'calendar-clock', label: 'Appointments' },
  { tab: 'payment-plans', icon: 'wallet', label: 'Payment Plans' },
  { tab: 'service-fees', icon: 'syringe', label: 'Service Fees' },
];

// Common North American zones; a stored zone outside this list is appended so
// the select always shows the saved value.
const COMMON_TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'America/St_Johns', label: 'Newfoundland (St. John\u2019s)' },
  { value: 'America/Halifax', label: 'Atlantic (Halifax)' },
  { value: 'America/Toronto', label: 'Eastern (Toronto)' },
  { value: 'America/Winnipeg', label: 'Central (Winnipeg)' },
  { value: 'America/Regina', label: 'Saskatchewan (Regina)' },
  { value: 'America/Edmonton', label: 'Mountain (Edmonton)' },
  { value: 'America/Vancouver', label: 'Pacific (Vancouver)' },
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
];

const TIMEZONE_OPTIONS = (current?: string) =>
  current && !COMMON_TIMEZONES.some(({ value }) => value === current)
    ? [...COMMON_TIMEZONES, { value: current, label: current }]
    : COMMON_TIMEZONES;

const SOCIAL_NETWORKS: Array<{ key: keyof TenantSocialLinks; label: string }> = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'Twitter / X' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'linkedin', label: 'LinkedIn' },
];

/**
 * Self-serve tenant options: the tenant-admin-editable subset of tenant
 * configuration (organization identity, contact details, links, social
 * icons, appointment options) plus the tenant-level pricing screens
 * (payment plans, service fees). Add a new entry to OPTION_TABS and a
 * route below to grow it.
 */
const TenantOptions: React.FC = () => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const params = usePathParams() as { optionsTab?: string };
  const activeTab = params.optionsTab || 'organization';
  const stripeEnabled = useAppSelector(IsStripeEnabled);
  const continuityEnabled = useAppSelector(IsContinuityOfCareEnabled);

  const [options, setOptions] = useState<TenantOptionsModel | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const result = await dispatch(api.Tenants.getTenantOptions(), {
      errorTitle: 'Options',
    });
    if (result) setOptions({ ...result, social_links: result.social_links ?? {} });
  };

  const updateField = <K extends keyof TenantOptionsModel>(
    field: K,
    value: TenantOptionsModel[K],
  ) => setOptions((current) => ({ ...current, [field]: value }));

  const updateLink = (network: keyof TenantSocialLinks, value: string) =>
    setOptions((current) => ({
      ...current,
      social_links: { ...current?.social_links, [network]: value },
    }));

  async function save() {
    if (!options) return;
    if (!options.name?.trim() || !options.name_short?.trim()) {
      MiscUtils.showToastrError('Options', 'Organization name and short name cannot be empty');
      return;
    }
    const invalid = SOCIAL_NETWORKS.find(({ key }) => {
      const value = options.social_links?.[key]?.trim();
      return !!value && !/^https:\/\/\S+\.\S+/.test(value);
    });
    if (invalid) {
      MiscUtils.showToastrError('Options', `${invalid.label} must be a full https:// link`);
      return;
    }
    setSaving(true);
    const result = await dispatch(api.Tenants.updateTenantOptions({ tenantOptions: options }), {
      title: 'Options',
      successText: 'Options saved!',
    });
    setSaving(false);
    if (result) setOptions({ ...result, social_links: result.social_links ?? {} });
  }

  function goToTab(tab: string) {
    nav.push(
      RouteUtils.getRoute(RouteUtils.routes().tenantOptions, {
        '/:optionsTab?': tab === 'organization' ? '' : '/' + tab,
      }),
    );
  }

  if (!options) return null;

  const textField = (label: string, field: keyof TenantOptionsModel, placeholder: string) => (
    <div className="row" key={field}>
      <DefaultTextField
        label={label}
        name={`tenant-options-${field}`}
        placeholder={placeholder}
        value={(options[field] as string | undefined) ?? ''}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          updateField(field, event.target.value)
        }
        style={{ width: '400px' }}
      />
    </div>
  );

  const tabs = OPTION_TABS.filter(({ tab }) => tab !== 'payment-plans' || stripeEnabled);

  const listIcons = tabs.map(({ tab, icon, label }) => (
    <li key={tab} id={'menu-' + tab} className="clickable" onClick={() => goToTab(tab)}>
      <Icon
        width="20px"
        src={icon}
        alt={label}
        showTooltip={true}
        tooltipPlace="right"
        className={tab === activeTab ? 'blue' : 'dark-grey'}
      />
    </li>
  ));

  const card = (
    title: string,
    hint: string | undefined,
    content: React.ReactNode,
    showSave = true,
  ) => (
    <div className="section">
      <span className="small-title">{title}</span>
      {hint && <p className="hint">{hint}</p>}
      {content}
      {showSave && (
        <div className="row button-wrapper">
          <DefaultButton name="Save" className="blue" onClick={save} loading={saving} />
        </div>
      )}
    </div>
  );

  const page = (content: React.ReactNode) => (
    <div className="full-height-panel full-width section-cards-layout">{content}</div>
  );

  return (
    <div className="tenant-options full-height full-width">
      <ul className="icon-list side-bar-vertical shadow-300">{listIcons}</ul>
      <div className="row extension">
        <NavStack
          stack={Stacks.Master}
          authenticateAll
          routes={{
            '*/contact': {
              element: page(
                card(
                  'Contact & Links',
                  'Contact details and website shown to patients across the portal and in emails.',
                  <>
                    {textField('Support Email', 'support_email', 'support@yourclinic.com')}
                    {textField('Info Email', 'info_email', 'info@yourclinic.com')}
                    {textField('Phone', 'default_phone', 'e.g. 647-555-0100')}
                    {textField('Website', 'company_website', 'https://yourclinic.com')}
                  </>,
                ),
              ),
            },
            '*/social': {
              element: page(
                card(
                  'Social Links',
                  'Shown as icons in the footer of every email sent to your patients. Leave a ' +
                    'network blank to hide its icon; with none configured the social row is ' +
                    'omitted entirely.',
                  SOCIAL_NETWORKS.map(({ key, label }) => (
                    <div className="row" key={key}>
                      <DefaultTextField
                        label={label}
                        name={`tenant-options-${key}`}
                        placeholder={`https://www.${
                          key === 'twitter' ? 'twitter' : key
                        }.com/yourpage`}
                        value={options.social_links?.[key] ?? ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          updateLink(key, event.target.value)
                        }
                        style={{ width: '400px' }}
                      />
                    </div>
                  )),
                ),
              ),
            },
            '*/appointments': {
              element: page(
                card(
                  'Appointments',
                  'Booking options that apply to your whole organization. Per-concern pricing, ' +
                    'delivery methods, and requirements are managed in Booking Flow and ' +
                    'Clearances.',
                  <>
                    <div className="option-block">
                      <div className="row">
                        <DefaultTextField
                          label="Cancellation Window (hours)"
                          name="tenant-options-refund-window"
                          type="number"
                          placeholder="e.g. 24"
                          value={
                            options.default_refund_window_hours != null
                              ? String(options.default_refund_window_hours)
                              : ''
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            updateField(
                              'default_refund_window_hours',
                              event.target.value === ''
                                ? undefined
                                : parseInt(event.target.value) || 0,
                            )
                          }
                          style={{ width: '220px' }}
                        />
                      </div>
                      <p className="field-hint">
                        How many hours before the appointment a patient can cancel and still receive
                        a credit (we never issue refunds — cancelling inside the window simply
                        forfeits the payment). Example: 24 means a patient must cancel at least 24
                        hours before the start time. Leave blank for the strictest policy:
                        cancellations are never credited. A concern with its own refund window in
                        Booking Flow overrides this default. Patients see the resulting policy when
                        they book.
                      </p>
                    </div>
                  </>,
                ),
              ),
            },
            '*/payment-plans': {
              element: page(
                card(
                  'Payment Plans',
                  'What each subscription plan includes. Appointment prices are managed per ' +
                    'concern in the booking flow, not here.',
                  <PaymentPlanSetup />,
                  false,
                ),
              ),
            },
            '*/service-fees': { element: <ServiceFees /> },
            '*': {
              element: page(
                card(
                  'Organization',
                  'Shown across the patient app and in every email sent to your patients.',
                  <>
                    {textField('Organization Name', 'name', 'e.g. Example Family Health')}
                    {textField('Short Name', 'name_short', 'e.g. Example')}
                    <div className="row">
                      <DefaultSelectField
                        label="Timezone"
                        name="tenant-options-timezone"
                        placeholder="Select a timezone"
                        value={options.timezone ?? ''}
                        onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                          updateField('timezone', (e.target.value as string) || undefined)
                        }
                        style={{ width: '400px' }}
                        inputElements={TIMEZONE_OPTIONS(options.timezone).map(
                          ({ value, label }) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ),
                        )}
                      />
                    </div>
                    <p className="field-hint">
                      The timezone your organization operates in. Appointment slots, reminders and
                      scheduled emails follow this zone. Defaults to Eastern Time (Toronto) until
                      set.
                    </p>
                    {textField('Staff Title', 'staff_title', 'e.g. Concierge, Care Coordinator')}
                    <p className="field-hint">
                      What patients see your admin/support staff called — in chat sender labels, the
                      chat button and staff role labels. Leave blank for the default ("Staff
                      Member").
                    </p>
                    <div className="row">
                      <DefaultSwitch
                        label="Prescription Refill Requests"
                        name="tenant-options-refill-requests"
                        disabled={!continuityEnabled}
                        value={continuityEnabled && options.refill_requests !== false}
                        checked={continuityEnabled && options.refill_requests !== false}
                        onChange={(_: any, checked: boolean) =>
                          updateField('refill_requests', checked)
                        }
                      />
                    </div>
                    <p className="field-hint">
                      {continuityEnabled ? (
                        <>
                          Lets patients request prescription refills from the app. Turning this off
                          hides the request button and stops the assistant offering refills — refill
                          requests already submitted stay in your queue and can still be actioned.
                        </>
                      ) : (
                        // Without continuity the effective gate is off no matter
                        // what this field says, so showing a live "On" switch
                        // would misreport the feature.
                        <>
                          Unavailable while Continuity of Care is off. A refill is routed to the
                          patient&apos;s primary practitioner, so it needs that model enabled.
                          Contact your administrator to change this.
                        </>
                      )}
                    </p>
                  </>,
                ),
              ),
            },
          }}
        />
      </div>
    </div>
  );
};

export default TenantOptions;
